import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import {
  analyzeFaqSuggestions,
  createAccountFAQ,
  fetchClaw9Accounts,
  fetchFaqRefusalStats,
  fetchFaqSuggestion,
  fetchFaqSuggestionStats,
  fetchFaqSuggestions,
  promoteFaqSuggestion,
  reviewFaqSuggestion,
  updateFaqSuggestion,
} from "../api/claw9";
import { useAppLanguage } from "../context/AppLanguageContext";

const TEXT = {
  en: {
    languageEnglish: "English",
    languageVietnamese: "Tiếng Việt",
    all: "All",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    promoted: "Promoted",
    high: "High",
    medium: "Medium",
    low: "Low",
    question: "Question",
    answer: "Answer",
    variations: "Variations",
    created: "Created",
    basedOn: "Based on",
    customerMessages: "customer messages",
    viewDetails: "View Details",
    approve: "Approve",
    reject: "Reject",
    promoteToFaqs: "Promote to FAQs",
    approveSuggestion: "Approve FAQ Suggestion?",
    rejectSuggestion: "Reject FAQ Suggestion?",
    approveHelp: "This FAQ will be used by the bot to answer similar customer questions. You can promote it to your main FAQ list later.",
    reasonOptional: "Reason (optional):",
    alreadyCovered: "Already covered by existing FAQ",
    cancel: "Cancel",
    saving: "Saving...",
    faqSuggestionDetail: "FAQ Suggestion Detail",
    status: "Status",
    priority: "Priority",
    addVariation: "+ Add variation",
    variationsHelp: "Edit or remove alternate phrasings the bot can match against.",
    noVariations: "No variations saved yet.",
    whySuggested: "Why this was suggested",
    noReason: "No reason was provided.",
    sampleMessages: "Sample customer messages",
    noSampleMessages: "No sample customer messages available.",
    reviewed: "Reviewed",
    saveChanges: "Save Changes",
    promoting: "Promoting...",
    noAccounts: "No Claw9 accounts found. Configure your account first.",
    loadError: "Failed to load FAQ self-improvement data.",
    detailError: "Failed to load suggestion details.",
    updateSuccess: "Suggestion updated successfully",
    updateFailed: "Failed to update suggestion",
    approveSuccess: "Suggestion approved successfully",
    rejectSuccess: "Suggestion rejected",
    reviewFailed: "Failed to review suggestion",
    noAccountPromotion: "No account found for FAQ promotion.",
    missingFaqId: "FAQ promotion succeeded but FAQ ID was missing.",
    promoteSuccess: "Suggestion promoted to FAQs",
    promoteFailed: "Failed to promote suggestion",
    analysisStarted: "Analysis started...",
    analysisFailed: "Failed to start analysis",
    analysisComplete: (count) => `Analysis complete: ${count} new suggestion${count === 1 ? "" : "s"}`,
    headerTitle: "FAQ Self-Improvement",
    headerDescription: "The AI analyzes customer messages you couldn't answer and suggests new FAQs to improve your bot.",
    analyzeNow: "Analyze Now",
    lastAnalysis: "Last analysis",
    refusalTitle: "Refusal Statistics (Last 7 days)",
    refusalDescription: "Track unanswered customer messages and analysis coverage.",
    totalRefusals: "Total Refusals",
    analyzed: "Analyzed",
    pendingAnalysis: "Pending Analysis",
    byReason: "By Reason",
    noRefusalBreakdown: "No refusal breakdown available.",
    searchSuggestions: "Search suggestions",
    loadingSuggestions: "Loading self-improvement suggestions...",
    perfectCoverage: "Perfect coverage!",
    perfectCoverageDescription: "No unanswered customer messages found. Your FAQs are covering all customer questions. Keep monitoring for new patterns.",
    allCaughtUp: "All caught up!",
    allCaughtUpDescription: "No pending FAQ suggestions to review. The AI analyzes customer messages daily and will suggest new FAQs when patterns are detected.",
    noMatchingSuggestions: "No matching suggestions",
    noMatchingSuggestionsDescription: "Try changing the status, priority, or search filters to find other FAQ suggestions.",
    loadingSuggestionDetail: "Loading suggestion detail...",
    minuteAgo: (count) => `${count} minute${count === 1 ? "" : "s"} ago`,
    hourAgo: (count) => `${count} hour${count === 1 ? "" : "s"} ago`,
    dayAgo: (count) => `${count} day${count === 1 ? "" : "s"} ago`,
    justNow: "just now",
    notYet: "Not yet",
    unknown: "Unknown",
  },
  vi: {
    languageEnglish: "English",
    languageVietnamese: "Tiếng Việt",
    all: "Tất cả",
    pending: "Chờ duyệt",
    approved: "Đã duyệt",
    rejected: "Đã từ chối",
    promoted: "Đã đưa vào FAQ",
    high: "Cao",
    medium: "Trung bình",
    low: "Thấp",
    question: "Câu hỏi",
    answer: "Câu trả lời",
    variations: "Biến thể",
    created: "Tạo lúc",
    basedOn: "Dựa trên",
    customerMessages: "tin nhắn khách hàng",
    viewDetails: "Xem chi tiết",
    approve: "Duyệt",
    reject: "Từ chối",
    promoteToFaqs: "Đưa vào FAQs",
    approveSuggestion: "Duyệt gợi ý FAQ?",
    rejectSuggestion: "Từ chối gợi ý FAQ?",
    approveHelp: "FAQ này sẽ được bot dùng để trả lời các câu hỏi tương tự. Bạn vẫn có thể đưa nó vào danh sách FAQ chính sau.",
    reasonOptional: "Lý do (không bắt buộc):",
    alreadyCovered: "Đã có FAQ khác bao phủ",
    cancel: "Hủy",
    saving: "Đang lưu...",
    faqSuggestionDetail: "Chi tiết gợi ý FAQ",
    status: "Trạng thái",
    priority: "Ưu tiên",
    addVariation: "+ Thêm biến thể",
    variationsHelp: "Chỉnh sửa hoặc xóa các cách diễn đạt khác mà bot có thể nhận diện.",
    noVariations: "Chưa có biến thể nào được lưu.",
    whySuggested: "Vì sao được gợi ý",
    noReason: "Chưa có lý do được cung cấp.",
    sampleMessages: "Tin nhắn mẫu từ khách hàng",
    noSampleMessages: "Chưa có tin nhắn mẫu.",
    reviewed: "Đã duyệt",
    saveChanges: "Lưu thay đổi",
    promoting: "Đang đưa vào FAQ...",
    noAccounts: "Không tìm thấy tài khoản Claw9. Hãy cấu hình tài khoản trước.",
    loadError: "Không thể tải dữ liệu tự cải thiện FAQ.",
    detailError: "Không thể tải chi tiết gợi ý.",
    updateSuccess: "Đã cập nhật gợi ý thành công",
    updateFailed: "Không thể cập nhật gợi ý",
    approveSuccess: "Đã duyệt gợi ý thành công",
    rejectSuccess: "Đã từ chối gợi ý",
    reviewFailed: "Không thể duyệt gợi ý",
    noAccountPromotion: "Không tìm thấy tài khoản để đưa vào FAQ.",
    missingFaqId: "Đã tạo FAQ nhưng thiếu FAQ ID.",
    promoteSuccess: "Đã đưa gợi ý vào FAQs",
    promoteFailed: "Không thể đưa gợi ý vào FAQs",
    analysisStarted: "Đã bắt đầu phân tích...",
    analysisFailed: "Không thể bắt đầu phân tích",
    analysisComplete: (count) => `Phân tích xong: ${count} gợi ý mới`,
    headerTitle: "FAQ Tự Cải Thiện",
    headerDescription: "AI phân tích những tin nhắn khách hàng mà bot chưa trả lời được và gợi ý FAQ mới để cải thiện bot.",
    analyzeNow: "Phân tích ngay",
    lastAnalysis: "Phân tích gần nhất",
    refusalTitle: "Thống kê từ chối (7 ngày qua)",
    refusalDescription: "Theo dõi các tin nhắn chưa trả lời được và mức độ đã phân tích.",
    totalRefusals: "Tổng số từ chối",
    analyzed: "Đã phân tích",
    pendingAnalysis: "Chờ phân tích",
    byReason: "Theo lý do",
    noRefusalBreakdown: "Chưa có thống kê lý do từ chối.",
    searchSuggestions: "Tìm gợi ý",
    loadingSuggestions: "Đang tải gợi ý tự cải thiện...",
    perfectCoverage: "Bao phủ hoàn hảo!",
    perfectCoverageDescription: "Không có tin nhắn khách hàng nào chưa được trả lời. Bộ FAQ hiện tại đang bao phủ tốt các câu hỏi.",
    allCaughtUp: "Đã xử lý xong!",
    allCaughtUpDescription: "Không còn gợi ý FAQ nào đang chờ duyệt. AI sẽ tiếp tục phân tích hàng ngày và tạo gợi ý mới khi phát hiện mẫu lặp lại.",
    noMatchingSuggestions: "Không có gợi ý phù hợp",
    noMatchingSuggestionsDescription: "Hãy đổi bộ lọc trạng thái, mức ưu tiên hoặc từ khóa tìm kiếm để xem các gợi ý khác.",
    loadingSuggestionDetail: "Đang tải chi tiết gợi ý...",
    minuteAgo: (count) => `${count} phút trước`,
    hourAgo: (count) => `${count} giờ trước`,
    dayAgo: (count) => `${count} ngày trước`,
    justNow: "vừa xong",
    notYet: "Chưa có",
    unknown: "Không rõ",
  },
};

const STATUS_OPTIONS = [
  { value: "", key: "all" },
  { value: "pending", key: "pending" },
  { value: "approved", key: "approved" },
  { value: "rejected", key: "rejected" },
  { value: "promoted", key: "promoted" },
];

const PRIORITY_OPTIONS = [
  { value: "", key: "all" },
  { value: "high", key: "high" },
  { value: "medium", key: "medium" },
  { value: "low", key: "low" },
];

const PRIORITY_META = {
  high: {
    label: "High",
    icon: "🔴",
    className: "bg-red-500/15 text-red-300 border-red-500/25",
  },
  medium: {
    label: "Medium",
    icon: "🟡",
    className: "bg-amber-500/15 text-amber-300 border-amber-500/25",
  },
  low: {
    label: "Low",
    icon: "🟢",
    className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  },
};

const STATUS_META = {
  pending: {
    label: "Pending",
    className: "bg-white/10 text-white/70 border-white/10",
  },
  approved: {
    label: "Approved",
    className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-500/15 text-red-300 border-red-500/25",
  },
  promoted: {
    label: "Promoted",
    className: "bg-violet-500/15 text-violet-300 border-violet-500/25",
  },
};

const normalizeSuggestion = (suggestion) => ({
  suggestion_id: suggestion?.suggestion_id || suggestion?.id || "",
  account_id: suggestion?.account_id || "",
  question: String(suggestion?.question || "").trim(),
  answer: String(suggestion?.answer || "").trim(),
  variations: Array.isArray(suggestion?.variations)
    ? suggestion.variations.map((variation) => String(variation || "").trim()).filter(Boolean)
    : [],
  reason: suggestion?.reason || null,
  priority: suggestion?.priority || "medium",
  status: suggestion?.status || "pending",
  source_refusal_ids: Array.isArray(suggestion?.source_refusal_ids) ? suggestion.source_refusal_ids : [],
  sample_messages: Array.isArray(suggestion?.sample_messages) ? suggestion.sample_messages : [],
  match_count: Number(suggestion?.match_count || 0),
  created_at: suggestion?.created_at || null,
  updated_at: suggestion?.updated_at || null,
  reviewed_at: suggestion?.reviewed_at || null,
  reviewed_by: suggestion?.reviewed_by || null,
  promoted_to_faq_id: suggestion?.promoted_to_faq_id || null,
});

const normalizeStats = (stats) => ({
  total: Number(stats?.total || 0),
  pending: Number(stats?.pending || stats?.by_status?.pending || 0),
  approved: Number(stats?.approved || stats?.by_status?.approved || 0),
  rejected: Number(stats?.rejected || stats?.by_status?.rejected || 0),
  promoted: Number(stats?.promoted || stats?.by_status?.promoted || 0),
  total_matches: Number(stats?.total_matches || 0),
  by_priority: stats?.by_priority || {},
  last_analysis_at: stats?.last_analysis_at || stats?.lastAnalyzedAt || null,
});

const normalizeRefusalStats = (stats) => ({
  total: Number(stats?.total || 0),
  analyzed: Number(stats?.analyzed || 0),
  pending: Number(stats?.pending || 0),
  by_reason: stats?.by_reason || {},
  period_days: Number(stats?.period_days || 7),
});

const formatRelativeTime = (timestamp, t) => {
  if (!timestamp) return t.notYet;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return t.unknown;

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes < 1) return t.justNow;
  if (diffMinutes < 60) return t.minuteAgo(diffMinutes);

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return t.hourAgo(diffHours);

  const diffDays = Math.round(diffHours / 24);
  return t.dayAgo(diffDays);
};

const StatCard = ({ label, value, icon, accentClass }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0a0f1c] p-4">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm text-white/50">{label}</p>
        <p className="mt-2 text-3xl font-semibold text-white">{value}</p>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${accentClass}`}>
        <span className="text-lg">{icon}</span>
      </div>
    </div>
  </div>
);

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Toasts = ({ toasts, onDismiss }) => (
  <div className="pointer-events-none fixed right-4 top-4 z-[70] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className="pointer-events-auto rounded-xl border border-white/10 bg-[#111827] px-4 py-3 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-white">{toast.message}</p>
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(toast.id)}
            className="text-white/40 transition-colors hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    ))}
  </div>
);

const SuggestionCard = ({
  suggestion,
  onViewDetails,
  onApprove,
  onReject,
  onPromote,
  isBusy,
  t,
}) => {
  const priorityMeta = PRIORITY_META[suggestion.priority] || PRIORITY_META.medium;
  const statusMeta = STATUS_META[suggestion.status] || STATUS_META.pending;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0f1c] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Badge className={priorityMeta.className}>
          <span>{priorityMeta.icon}</span>
          {t[priorityMeta.label.toLowerCase()].toUpperCase()}
        </Badge>
        <Badge className={statusMeta.className}>{t[statusMeta.label.toLowerCase()]}</Badge>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-white/35">{t.question}</p>
          <p className="mt-1 text-base font-medium text-white">{suggestion.question}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-white/35">{t.answer}</p>
          <p className="mt-1 text-sm text-white/70">{suggestion.answer}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/45">
        <span>{t.variations}: {suggestion.variations.length}</span>
        <span>{t.basedOn}: {suggestion.match_count} {t.customerMessages}</span>
        <span>{t.created}: {formatRelativeTime(suggestion.created_at, t)}</span>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          aria-label={`View details for suggestion ${suggestion.question}`}
          onClick={() => onViewDetails(suggestion)}
          className="rounded-lg bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
        >
          {t.viewDetails}
        </button>
        {suggestion.status === "pending" && (
          <>
            <button
              type="button"
              aria-label={`Approve suggestion ${suggestion.question}`}
              onClick={() => onApprove(suggestion)}
              disabled={isBusy}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.approve}
            </button>
            <button
              type="button"
              aria-label={`Reject suggestion ${suggestion.question}`}
              onClick={() => onReject(suggestion)}
              disabled={isBusy}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.reject}
            </button>
          </>
        )}
        {suggestion.status === "approved" && (
          <button
            type="button"
            aria-label={`Promote suggestion ${suggestion.question} to FAQs`}
            onClick={() => onPromote(suggestion)}
            disabled={isBusy}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm text-white transition-colors hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {t.promoteToFaqs}
          </button>
        )}
      </div>
    </div>
  );
};

const ReviewDialog = ({ suggestion, mode, onCancel, onConfirm, isSubmitting }) => {
  const { language } = useAppLanguage();
  const t = TEXT[language] || TEXT.en;
  const [reason, setReason] = useState("");

  useEffect(() => {
    setReason("");
  }, [suggestion, mode]);

  if (!suggestion || !mode) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white">
          {mode === "approve" ? t.approveSuggestion : t.rejectSuggestion}
        </h3>
        {mode === "approve" ? (
          <p className="mt-3 text-sm text-white/65">
            {t.approveHelp}
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm text-white/65">{t.reasonOptional}</p>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-xl border border-white/10 bg-[#050810] px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              placeholder={t.alreadyCovered}
            />
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:bg-white/5"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={isSubmitting}
            className={`rounded-lg px-4 py-2 text-sm text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              mode === "approve" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"
            }`}
          >
            {isSubmitting ? t.saving : mode === "approve" ? t.approve : t.reject}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuggestionDetailModal = ({
  suggestion,
  onClose,
  onSave,
  onApprove,
  onReject,
  onPromote,
  isSaving,
  isReviewing,
  isPromoting,
}) => {
  const { language } = useAppLanguage();
  const t = TEXT[language] || TEXT.en;
  const [draft, setDraft] = useState(suggestion);

  useEffect(() => {
    setDraft(suggestion);
  }, [suggestion]);

  if (!suggestion || !draft) return null;

  const priorityMeta = PRIORITY_META[draft.priority] || PRIORITY_META.medium;
  const statusMeta = STATUS_META[draft.status] || STATUS_META.pending;

  const updateVariation = (index, value) => {
    setDraft((current) => ({
      ...current,
      variations: current.variations.map((variation, variationIndex) => (
        variationIndex === index ? value : variation
      )),
    }));
  };

  const removeVariation = (index) => {
    setDraft((current) => ({
      ...current,
      variations: current.variations.filter((_, variationIndex) => variationIndex !== index),
    }));
  };

  const addVariation = () => {
    setDraft((current) => ({
      ...current,
      variations: [...current.variations, ""],
    }));
  };

  return (
    <div className="fixed inset-0 z-[75] overflow-y-auto bg-black/75 p-4">
      <div className="mx-auto mt-6 w-full max-w-4xl rounded-3xl border border-white/10 bg-[#0d1424] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">{t.faqSuggestionDetail}</h3>
          <button
            type="button"
            aria-label="Close suggestion detail"
            onClick={onClose}
            className="rounded-lg p-2 text-white/50 transition-colors hover:bg-white/5 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={statusMeta.className}>{t.status}: {t[statusMeta.label.toLowerCase()]}</Badge>
            <Badge className={priorityMeta.className}>
              <span>{priorityMeta.icon}</span>
              {t.priority}: {t[priorityMeta.label.toLowerCase()]}
            </Badge>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-[#050810]/70 p-4">
              <label className="block text-xs font-medium uppercase tracking-wide text-white/40">
                {t.question}
              </label>
              <textarea
                value={draft.question}
                onChange={(event) => setDraft((current) => ({ ...current, question: event.target.value }))}
                rows={3}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#050810] px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              />
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#050810]/70 p-4">
              <label className="block text-xs font-medium uppercase tracking-wide text-white/40">
                {t.answer}
              </label>
              <textarea
                value={draft.answer}
                onChange={(event) => setDraft((current) => ({ ...current, answer: event.target.value }))}
                rows={5}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#050810] px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#050810]/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-medium text-white">{t.variations}</h4>
                <p className="mt-1 text-xs text-white/45">{t.variationsHelp}</p>
              </div>
              <button
                type="button"
                onClick={addVariation}
                className="rounded-lg bg-white/5 px-3 py-2 text-xs text-white transition-colors hover:bg-white/10"
              >
                {t.addVariation}
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {draft.variations.length ? draft.variations.map((variation, index) => (
                <div key={`${index}-${variation}`} className="flex items-center gap-2">
                  <input
                    value={variation}
                    onChange={(event) => updateVariation(index, event.target.value)}
                    className="flex-1 rounded-xl border border-white/10 bg-[#050810] px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
                  />
                  <button
                    type="button"
                    aria-label={`Remove variation ${index + 1}`}
                    onClick={() => removeVariation(index)}
                    className="rounded-lg p-2 text-white/50 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )) : (
                <p className="text-sm text-white/45">{t.noVariations}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-white/10 bg-[#050810]/70 p-4">
              <h4 className="text-sm font-medium text-white">{t.whySuggested}</h4>
              <p className="mt-2 text-sm text-white/70">{draft.reason || t.noReason}</p>
              <h5 className="mt-5 text-xs font-medium uppercase tracking-wide text-white/40">
                {t.sampleMessages}
              </h5>
              <div className="mt-2 space-y-2">
                {draft.sample_messages.length ? draft.sample_messages.map((message, index) => (
                  <div key={`${index}-${message}`} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/70">
                    {message}
                  </div>
                )) : (
                  <p className="text-sm text-white/45">{t.noSampleMessages}</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#050810]/70 p-4">
              <label className="block text-xs font-medium uppercase tracking-wide text-white/40">
                {t.priority}
              </label>
              <select
                value={draft.priority}
                onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#050810] px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              >
                <option value="high">{t.high}</option>
                <option value="medium">{t.medium}</option>
                <option value="low">{t.low}</option>
              </select>

              <div className="mt-6 space-y-2 text-xs text-white/45">
                <p>{t.basedOn} {draft.match_count} {t.customerMessages}</p>
                <p>{t.created} {formatRelativeTime(draft.created_at, t)}</p>
                {draft.reviewed_at ? <p>{t.reviewed} {formatRelativeTime(draft.reviewed_at, t)}</p> : null}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t border-white/10 pt-5">
            <button
              type="button"
              onClick={() => onSave(draft)}
              disabled={isSaving}
              className="rounded-lg bg-white/5 px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? t.saving : t.saveChanges}
            </button>
            {draft.status === "pending" ? (
              <>
                <button
                  type="button"
                  onClick={() => onApprove(draft)}
                  disabled={isReviewing}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t.approve}
                </button>
                <button
                  type="button"
                  onClick={() => onReject(draft)}
                  disabled={isReviewing}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t.reject}
                </button>
              </>
            ) : null}
            {draft.status === "approved" ? (
              <button
                type="button"
                onClick={() => onPromote(draft)}
                disabled={isPromoting}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm text-white transition-colors hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isPromoting ? t.promoting : t.promoteToFaqs}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const Claw9FaqSelfImprovement = () => {
  const { language } = useAppLanguage();
  const t = TEXT[language] || TEXT.en;
  const [accountId, setAccountId] = useState(null);
  const [stats, setStats] = useState(normalizeStats({}));
  const [refusalStats, setRefusalStats] = useState(normalizeRefusalStats({}));
  const [suggestions, setSuggestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [isRefusalExpanded, setIsRefusalExpanded] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isSavingSuggestion, setIsSavingSuggestion] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ mode: null, suggestion: null });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [promotionSubmittingId, setPromotionSubmittingId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [lastAnalysisAt, setLastAnalysisAt] = useState(null);

  const pushToast = useCallback((message) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((current) => [...current, { id, message }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4500);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const loadPageData = useCallback(async (options = {}) => {
    const { silent = false } = options;

    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const [accountsResult, statsResult, refusalResult, suggestionsResult] = await Promise.all([
        fetchClaw9Accounts(),
        fetchFaqSuggestionStats().catch((err) => {
          console.warn('[FaqSelfImprovement] Stats API not available:', err.message);
          return { data: { pending: 0, approved: 0, rejected: 0, implemented: 0, total: 0 } };
        }),
        fetchFaqRefusalStats(7).catch((err) => {
          console.warn('[FaqSelfImprovement] Refusal stats API not available:', err.message);
          return { data: { total_refusals: 0, top_reasons: [], trend: [] } };
        }),
        fetchFaqSuggestions({
          status: statusFilter || undefined,
          priority: priorityFilter || undefined,
          limit: 50,
        }).catch((err) => {
          console.warn('[FaqSelfImprovement] Suggestions API not available:', err.message);
          return { suggestions: [] };
        }),
      ]);

      const accounts = accountsResult?.accounts || [];
      if (!accounts.length) {
        throw new Error(t.noAccounts);
      }

      setAccountId(accounts[0].account_id);

      const normalizedStats = normalizeStats(statsResult?.data || statsResult);
      setStats(normalizedStats);
      setLastAnalysisAt(normalizedStats.last_analysis_at || null);
      setRefusalStats(normalizeRefusalStats(refusalResult?.data || refusalResult));

      const suggestionRows = (suggestionsResult?.data?.suggestions || suggestionsResult?.suggestions || suggestionsResult?.data || [])
        .map(normalizeSuggestion);
      setSuggestions(suggestionRows);
    } catch (loadError) {
      setError(loadError.message || t.loadError);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [priorityFilter, statusFilter, t.loadError, t.noAccounts]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const visibleSuggestions = useMemo(() => {
    if (!searchTerm.trim()) return suggestions;
    const query = searchTerm.trim().toLowerCase();
    return suggestions.filter((suggestion) =>
      suggestion.question.toLowerCase().includes(query) ||
      suggestion.answer.toLowerCase().includes(query) ||
      suggestion.sample_messages.some((message) => message.toLowerCase().includes(query))
    );
  }, [searchTerm, suggestions]);

  const openSuggestionDetail = async (suggestion) => {
    setSelectedSuggestion(normalizeSuggestion(suggestion));
    setDetailLoading(true);
    try {
      const detail = await fetchFaqSuggestion(suggestion.suggestion_id);
      setSelectedSuggestion(normalizeSuggestion(detail?.data || detail));
    } catch (detailError) {
      pushToast(detailError.message || t.detailError);
    } finally {
      setDetailLoading(false);
    }
  };

  const syncSuggestionInList = useCallback((updatedSuggestion) => {
    setSuggestions((current) => current.map((suggestion) => (
      suggestion.suggestion_id === updatedSuggestion.suggestion_id ? updatedSuggestion : suggestion
    )));
  }, []);

  const handleSaveSuggestion = async (draft) => {
    setIsSavingSuggestion(true);
    try {
      const result = await updateFaqSuggestion(draft.suggestion_id, {
        question: draft.question,
        answer: draft.answer,
        variations: draft.variations.filter(Boolean),
        priority: draft.priority,
      });
      const updated = normalizeSuggestion(result?.data || result);
      setSelectedSuggestion(updated);
      syncSuggestionInList(updated);
      pushToast(t.updateSuccess);
    } catch (saveError) {
      pushToast(saveError.message || t.updateFailed);
    } finally {
      setIsSavingSuggestion(false);
    }
  };

  const submitReview = async (reason = "") => {
    if (!reviewDialog.suggestion || !reviewDialog.mode) return;

    setReviewSubmitting(true);
    try {
      const payload = reviewDialog.mode === "approve"
        ? { action: "approve" }
        : { action: "reject", rejection_reason: reason || undefined };
      const result = await reviewFaqSuggestion(reviewDialog.suggestion.suggestion_id, payload);
      const updated = normalizeSuggestion(result?.data || result?.suggestion || {
        ...reviewDialog.suggestion,
        status: reviewDialog.mode === "approve" ? "approved" : "rejected",
      });
      syncSuggestionInList(updated);
      setSelectedSuggestion((current) => (
        current?.suggestion_id === updated.suggestion_id ? updated : current
      ));
      setReviewDialog({ mode: null, suggestion: null });
      await loadPageData({ silent: true });
      pushToast(reviewDialog.mode === "approve" ? t.approveSuccess : t.rejectSuccess);
    } catch (reviewError) {
      pushToast(reviewError.message || t.reviewFailed);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handlePromote = async (suggestion) => {
    if (!accountId) {
      pushToast(t.noAccountPromotion);
      return;
    }

    setPromotionSubmittingId(suggestion.suggestion_id);
    try {
      const createResult = await createAccountFAQ(accountId, {
        question: suggestion.question,
        answer: suggestion.answer,
        category: "all",
      });
      const createdFaq = createResult?.data || createResult;
      const faqId = createdFaq?.id || createdFaq?.faq_id;

      if (!faqId) {
        throw new Error(t.missingFaqId);
      }

      await promoteFaqSuggestion(suggestion.suggestion_id, faqId);

      const updatedSuggestion = {
        ...suggestion,
        status: "promoted",
        promoted_to_faq_id: faqId,
      };
      syncSuggestionInList(updatedSuggestion);
      setSelectedSuggestion((current) => (
        current?.suggestion_id === suggestion.suggestion_id ? updatedSuggestion : current
      ));
      await loadPageData({ silent: true });
      pushToast(t.promoteSuccess);
    } catch (promotionError) {
      pushToast(promotionError.message || t.promoteFailed);
    } finally {
      setPromotionSubmittingId(null);
    }
  };

  const handleAnalyzeNow = async () => {
    setIsAnalyzing(true);
    setError(null);
    pushToast(t.analysisStarted);
    try {
      const result = await analyzeFaqSuggestions();
      const resultData = result?.data || result;
      const newSuggestions = Number(
        resultData?.new_suggestions ||
        resultData?.created ||
        resultData?.suggestions_created ||
        0
      );
      setLastAnalysisAt(resultData?.analyzed_at || resultData?.completed_at || new Date().toISOString());
      await loadPageData({ silent: true });
      pushToast(t.analysisComplete(newSuggestions));
    } catch (analysisError) {
      pushToast(analysisError.message || t.analysisFailed);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const showPerfectCoverage = !visibleSuggestions.length && refusalStats.total === 0;
  const showAllCaughtUp = !visibleSuggestions.length && !searchTerm && statusFilter === "pending" && refusalStats.total > 0;

  return (
    <div className="flex h-full flex-col">
      <Toasts toasts={toasts} onDismiss={removeToast} />

      {error ? (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-200/70 transition-colors hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      <div className="rounded-3xl border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(255,77,77,0.22),transparent_35%),linear-gradient(180deg,#10172a_0%,#0a0f1c_100%)] px-6 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="h-5 w-5 text-[#ff4d4d]" />
              <h1 className="text-2xl font-semibold">{t.headerTitle}</h1>
            </div>
            <p className="mt-3 max-w-2xl text-sm text-white/70">
              {t.headerDescription}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              aria-label={t.analyzeNow}
              onClick={handleAnalyzeNow}
              disabled={isAnalyzing}
              className="inline-flex items-center gap-2 rounded-xl bg-[#ff4d4d] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#ff5c5c] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t.analyzeNow}
            </button>
            <div className="text-sm text-white/55">
              {t.lastAnalysis}: {formatRelativeTime(lastAnalysisAt, t)}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t.pending} value={stats.pending} icon="🕐" accentClass="bg-white/10" />
        <StatCard label={t.approved} value={stats.approved} icon="✅" accentClass="bg-emerald-500/15" />
        <StatCard label={t.rejected} value={stats.rejected} icon="❌" accentClass="bg-red-500/15" />
        <StatCard label={t.promoted} value={stats.promoted} icon="📤" accentClass="bg-violet-500/15" />
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0f1c]">
        <button
          type="button"
          aria-label={t.refusalTitle}
          onClick={() => setIsRefusalExpanded((current) => !current)}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <p className="text-base font-medium text-white">{t.refusalTitle}</p>
            <p className="mt-1 text-sm text-white/45">{t.refusalDescription}</p>
          </div>
          <ChevronDown className={`h-5 w-5 text-white/45 transition-transform ${isRefusalExpanded ? "rotate-180" : ""}`} />
        </button>
        {isRefusalExpanded ? (
          <div className="grid gap-4 border-t border-white/10 px-5 py-4 md:grid-cols-2">
            <div className="space-y-2 text-sm text-white/70">
              <p>{t.totalRefusals}: <span className="text-white">{refusalStats.total}</span></p>
              <p>{t.analyzed}: <span className="text-white">{refusalStats.analyzed}</span> ({refusalStats.total ? Math.round((refusalStats.analyzed / refusalStats.total) * 100) : 0}%)</p>
              <p>{t.pendingAnalysis}: <span className="text-white">{refusalStats.pending}</span></p>
            </div>
            <div>
              <p className="text-sm font-medium text-white">{t.byReason}</p>
              <div className="mt-2 space-y-2 text-sm text-white/65">
                {Object.keys(refusalStats.by_reason).length ? Object.entries(refusalStats.by_reason).map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2">
                    <span>{reason}</span>
                    <span className="text-white">{count}</span>
                  </div>
                )) : (
                  <p className="text-white/45">{t.noRefusalBreakdown}</p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0f1c] p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-white/50">{t.status}:</span>
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value || "all"}
                type="button"
                aria-label={`${t.status}: ${t[option.key]}`}
                onClick={() => setStatusFilter(option.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === option.value
                    ? "bg-[#ff4d4d] text-white"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}
              >
                {t[option.key]}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-white/50">{t.priority}:</span>
            <select
              value={priorityFilter}
              onChange={(event) => setPriorityFilter(event.target.value)}
              aria-label={t.priority}
              className="rounded-xl border border-white/10 bg-[#050810] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
            >
              {PRIORITY_OPTIONS.map((option) => (
                <option key={option.value || "all-priorities"} value={option.value}>
                  {t[option.key]}
                </option>
              ))}
            </select>
          </div>

          <div className="relative ml-auto min-w-[16rem] flex-1 md:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t.searchSuggestions}
              aria-label={t.searchSuggestions}
              className="w-full rounded-xl border border-white/10 bg-[#050810] py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
            />
          </div>

          <button
            type="button"
            aria-label={t.searchSuggestions}
            onClick={() => loadPageData({ silent: true })}
            disabled={isRefreshing}
            className="rounded-xl bg-white/5 p-2.5 text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="mt-6 flex-1 overflow-y-auto pr-1">
        {isLoading ? (
          <div className="flex h-64 flex-col items-center justify-center text-white/50">
            <Loader2 className="h-8 w-8 animate-spin text-[#ff4d4d]" />
            <p className="mt-3 text-sm">{t.loadingSuggestions}</p>
          </div>
        ) : showPerfectCoverage ? (
          <div className="flex min-h-[22rem] flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-[#0a0f1c] px-6 text-center">
            <div className="text-5xl">🎯</div>
            <p className="mt-5 text-2xl font-semibold text-white">{t.perfectCoverage}</p>
            <p className="mt-3 max-w-xl text-sm text-white/55">
              {t.perfectCoverageDescription}
            </p>
          </div>
        ) : showAllCaughtUp ? (
          <div className="flex min-h-[22rem] flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-[#0a0f1c] px-6 text-center">
            <div className="text-5xl">✨</div>
            <p className="mt-5 text-2xl font-semibold text-white">{t.allCaughtUp}</p>
            <p className="mt-3 max-w-xl text-sm text-white/55">
              {t.allCaughtUpDescription}
            </p>
            <button
              type="button"
              onClick={handleAnalyzeNow}
              disabled={isAnalyzing}
              className="mt-6 rounded-xl bg-[#ff4d4d] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#ff5c5c] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.analyzeNow}
            </button>
          </div>
        ) : visibleSuggestions.length ? (
          <div className="space-y-4">
            {visibleSuggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.suggestion_id}
                suggestion={suggestion}
                t={t}
                onViewDetails={openSuggestionDetail}
                onApprove={(item) => setReviewDialog({ mode: "approve", suggestion: item })}
                onReject={(item) => setReviewDialog({ mode: "reject", suggestion: item })}
                onPromote={handlePromote}
                isBusy={reviewSubmitting || promotionSubmittingId === suggestion.suggestion_id}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[22rem] flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-[#0a0f1c] px-6 text-center">
            <div className="text-5xl">🔎</div>
            <p className="mt-5 text-2xl font-semibold text-white">{t.noMatchingSuggestions}</p>
            <p className="mt-3 max-w-xl text-sm text-white/55">
              {t.noMatchingSuggestionsDescription}
            </p>
          </div>
        )}
      </div>

      {detailLoading && selectedSuggestion ? (
        <div className="fixed inset-0 z-[74] flex items-center justify-center bg-black/50">
          <div className="rounded-2xl border border-white/10 bg-[#0d1424] px-5 py-4 text-white">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#ff4d4d]" />
            <p className="mt-2 text-sm text-white/60">{t.loadingSuggestionDetail}</p>
          </div>
        </div>
      ) : null}

      {!detailLoading && selectedSuggestion ? (
        <SuggestionDetailModal
          suggestion={selectedSuggestion}
          onClose={() => setSelectedSuggestion(null)}
          onSave={handleSaveSuggestion}
          onApprove={(suggestion) => setReviewDialog({ mode: "approve", suggestion })}
          onReject={(suggestion) => setReviewDialog({ mode: "reject", suggestion })}
          onPromote={handlePromote}
          isSaving={isSavingSuggestion}
          isReviewing={reviewSubmitting}
          isPromoting={promotionSubmittingId === selectedSuggestion.suggestion_id}
        />
      ) : null}

      <ReviewDialog
        suggestion={reviewDialog.suggestion}
        mode={reviewDialog.mode}
        onCancel={() => setReviewDialog({ mode: null, suggestion: null })}
        onConfirm={submitReview}
        isSubmitting={reviewSubmitting}
      />
    </div>
  );
};

export default Claw9FaqSelfImprovement;
